import React from 'react';
import { 
  BarChart3, 
  Database, 
  FileSearch, 
  PieChart, 
  Settings, 
  GraduationCap
} from 'lucide-react';

export const Services = () => {
  const services = [
    {
      icon: BarChart3,
      title: "Performance Monitoring",
      description: "We design comprehensive monitoring systems that track progress and drive development outcomes across contexts.",
      features: ["Logic Model Design", "KPI Framework", "Real-time Dashboards", "Progress Tracking systems"],
      gradient: "gradient-primary",
      accentColor: "text-teal-600"
    },
    {
      icon: Database,
      title: "Data collection & Analytics",
      description: "We offer advanced data collection and analysis services that transform information into powerful insights.",
      features: ["Survey Design", "Data Collection", "Statistical Analysis", "Insight Generation"],
      gradient: "gradient-warm",
      accentColor: "text-orange-600"
    },
    {
      icon: FileSearch,
      title: "Evaluation",
      description: "We employ rigorous evaluation methodologies to determine program effectiveness and sustainable impact.",
      features: ["Evaluation design", "Baseline Studies", "Performance and process evaluation", "Impact Assessment"],
      gradient: "gradient-nature",
      accentColor: "text-emerald-600"
    },
    {
      icon: Settings,
      title: "Digital Solutions",
      description: "We develop custom digital tools and platforms designed for effective data management and reporting.",
      features: ["Web Platforms", "Mobile Solutions", "Data Dashboards", "Geo-spatial solutions"],
      gradient: "gradient-primary",
      accentColor: "text-teal-600"
    },
    {
      icon: GraduationCap,
      title: "Capacity Building",
      description: "We offer a comprehensive training and development programs that strengthen organizational M&E capabilities.",
      features: ["M&E Capacity Assessment", "M&E Training", "Skills Development", "Mentoring Programs"],
      gradient: "gradient-sunset",
      accentColor: "text-red-600"
    },
    {
      icon: PieChart,
      title: "Strategy Development",
      description: "We offer strategic planning and program design services that maximize development outcomes.",
      features: ["Gaps analysis", "Scenario Planning", "Stakeholder Engagement", "Strategy/Program Design"],
      gradient: "gradient-earth",
      accentColor: "text-amber-600"
    }
  ];

  return (
    <section id="services" className="section-padding bg-white relative overflow-hidden">
      
      {/* Background elements */}
      <div className="absolute inset-0 pattern-african opacity-20"></div>
      <div className="absolute top-1/4 right-1/3 w-96 h-96 gradient-warm rounded-full opacity-5 blur-3xl"></div>
      <div className="absolute bottom-1/3 left-1/4 w-80 h-80 gradient-nature rounded-full opacity-5 blur-3xl"></div>
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        
        {/* Services header */}
        <div className="text-center mb-20 animate-fadeInUp">
          <h2 className="text-section text-gray-900 mb-8 leading-tight">
            <span className="text-gradient-primary">Services</span>
          </h2>
        </div>

        {/* Services grid with African energy */}
        <div className="grid lg:grid-cols-3 gap-10 mb-20">
          {services.map((service, index) => {
            const Icon = service.icon;
            
            return (
              <div 
                key={index} 
                className={`animate-fadeInUp`}
                style={{animationDelay: `${index * 0.15}s`}}
              >
                <div className="card-modern group h-full hover:scale-105 transition-all duration-500">
                  
                  {/* Icon with African-inspired gradient */}
                  <div className="relative mb-8">
                    <div className={`w-16 h-16 ${service.gradient} rounded-3xl flex items-center justify-center shadow-xl group-hover:shadow-2xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className={`absolute -inset-2 ${service.gradient} rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-300`}></div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-gradient-primary transition-all duration-300">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed mb-8 font-medium">
                    {service.description}
                  </p>

                  {/* Features with African dots */}
                  <ul className="space-y-4 mb-10">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center group/item">
                        <div className={`w-2.5 h-2.5 ${service.gradient} rounded-full mr-4 group-hover/item:scale-150 transition-transform duration-300`}></div>
                        <span className="text-gray-700 font-medium group-hover/item:text-gray-900 transition-colors duration-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
 
                </div>
              </div>
            );
          })}
        </div>

 
      </div>
    </section>
  );
};